/** @jsxImportSource @emotion/react */
import { FormProvider, useForm } from "react-hook-form";
import * as yup from "yup";
import { yupResolver } from "@hookform/resolvers/yup";
import { useSelector } from "react-redux";
import { toastr } from "react-redux-toastr";
import * as authSelectors from "../../auth/authSelectors";
import {
  IntegrationPayload,
  IntegrationListItem,
} from "../models/integrations";
import {
  useCreateIntegration,
  useGetIntegration,
  useGetIntegrationProviders,
  useUpdateIntegration,
} from "../service/integrations-api";
import { Drawer } from "../../../components/Drawer";
import { ButtonAction } from "../../../components/NewButtons";
import { ActionSpinner } from "../../../components/ActionSpinner";
import { StyledForm } from "../../../components/StyledForm";
import { SelectInput } from "../../../components/forms/inputs/SelectInput";
import { css } from "@emotion/react";
import { SpinnerBlock } from "../../common/Spinner";
import { TextInput } from "../../../components/forms/inputs/TextInput";
import { SwitchInput } from "../../../components/forms/inputs/SwitchInput";
import { getErrorMessage, useHandleFetchError } from "../../../error-handler";
import { useEffect } from "react";

type Props = {
  isOpen: boolean;
  onClose: () => void;
  integration?: IntegrationListItem;
};

const additionalFormStyles = css`
  gap: 16px;
`;

const validationSchema = yup.object({
  baseUrl: yup.string().nullable().optional(),
  clientPassword: yup.string().nullable().optional(),
  clientSecret: yup.string().nullable().optional(),
  clientUsername: yup.string().nullable().optional(),
  clientId: yup.string().nullable().optional(),
  enabled: yup.boolean().required("Choose if the integration is enabled"),
  sandbox: yup
    .boolean()
    .required("Choose if the integration is using a sandbox environment"),
  name: yup.string().required("Name is required"),
  providerDescriptionId: yup
    .string()
    .required("Provider is required to continue"),
});

export const MutateIntegrationDrawer = ({
  isOpen,
  onClose,
  integration,
}: Props) => {
  const organizationKeyName = useSelector(
    authSelectors.selectedOrganizationKeyName
  );
  const methods = useForm<IntegrationPayload>({
    defaultValues: {
      baseUrl: "",
      clientId: "",
      clientPassword: "",
      clientSecret: "",
      clientUsername: "",
      enabled: true,
      sandbox: false,
      name: "",
      providerDescriptionId: "",
    },
    resolver: yupResolver(validationSchema),
  });

  const { reset } = methods;

  const isEdit = !!integration;

  const {
    data: integrationProviders,
    isLoading,
    error,
  } = useGetIntegrationProviders(organizationKeyName);

  const {
    data: integrationData,
    isLoading: isLoadingIntegration,
  } = useGetIntegration(organizationKeyName, integration?.id);

  const { mutateAsync: createIntegration } = useCreateIntegration();

  const { mutateAsync: updateIntegration } = useUpdateIntegration();

  const handleOnClose = () => {
    methods.reset();
    onClose();
  };

  const onSubmit = async (data: IntegrationPayload) => {
    if (!data.baseUrl) {
      data.baseUrl = null;
    }
    if (!data.clientPassword) {
      data.clientPassword = null;
    }
    if (!data.clientSecret) {
      data.clientSecret = null;
    }
    if (!data.clientUsername) {
      data.clientUsername = null;
    }
    if (!data.clientId) {
      data.clientId = null;
    }
    try {
      if (isEdit) {
        await updateIntegration({
          organizationKeyName,
          data: {
            id: integration.id,
            ...data,
          },
        });
      } else {
        await createIntegration({
          organizationKeyName,
          data,
        });
      }
      toastr.success(
        "Success",
        `${isEdit ? "Edited" : "Created"} integration successfully`
      );
      handleOnClose();
    } catch (error: unknown) {
      const message = getErrorMessage(error);
      toastr.error("Error", message);
    }
  };

  const integrationProvidersOptions = (integrationProviders || []).map(
    (provider) => ({
      value: provider.id,
      label: provider.id,
    })
  );
  integrationProvidersOptions?.unshift({ value: "", label: "None" });

  useHandleFetchError(error);

  useEffect(() => {
    if (isLoadingIntegration) {
      return;
    }

    if (!integrationData) {
      return;
    }
    const { provider, ...rest } = integrationData;
    reset({
      ...rest,
      providerDescriptionId: provider,
    });
  }, [integrationData, reset, isLoadingIntegration]);

  return (
    <Drawer
      title={`${isEdit ? "Edit" : "Create"} integration`}
      width={500}
      buttons={
        <ButtonAction
          disabled={methods.formState.isSubmitting}
          onClick={methods.handleSubmit(onSubmit)}
          type="button"
        >
          {methods.formState.isSubmitting ? <ActionSpinner /> : "Save"}
        </ButtonAction>
      }
      open={isOpen}
      onClose={handleOnClose}
    >
      <FormProvider {...methods}>
        {isLoading || isLoadingIntegration ? (
          <SpinnerBlock />
        ) : (
          <StyledForm additionalStyles={additionalFormStyles}>
            <SelectInput
              name="providerDescriptionId"
              label="Provider"
              options={integrationProvidersOptions}
              helperText="Choose the platform for the sales channel"
            />
            <TextInput
              label="Integration name"
              name="name"
              helperText="Enter the name of the integration"
            />
            <TextInput
              label="Base URL"
              name="baseUrl"
              helperText="The base URL we should use for the integration"
            />
            <TextInput
              label="Client ID"
              name="clientId"
              helperText="The client ID for the integration"
            />
            <TextInput
              label="Client username"
              name="clientUsername"
              helperText="The client username for the integration"
            />
            <TextInput
              label="Client password"
              name="clientPassword"
              helperText="The client password for the integration"
            />
            <TextInput
              label="Client secret"
              name="clientSecret"
              helperText="The client secret for the integration"
            />
            <SwitchInput name="enabled" label="Is enabled" />
            <SwitchInput name="sandbox" label="Is sandbox" />
          </StyledForm>
        )}
      </FormProvider>
    </Drawer>
  );
};
